import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Services" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled ? "bg-white/70 backdrop-blur-xl border-b border-black/5 py-4" : "bg-transparent py-6"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 flex items-center justify-between">
          <Logo className="h-10 sm:h-12" />

          <nav className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s|à/g, "")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide hover-line ${isActive ? "text-violet" : "text-ink/80 hover:text-ink"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <Link
            to="/contact"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center gap-2 bg-ink text-white text-sm px-6 py-3 hover:bg-violet transition-colors duration-300"
          >
            Démarrer un projet
          </Link>

          <button
            className="md:hidden text-ink p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white pt-28 px-6 md:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `font-display text-4xl font-extrabold tracking-tight py-3 border-b border-black/5 ${
                      isActive ? "text-violet" : "text-ink"
                    }`
                  }
                >
                  {String(i + 1).padStart(2, "0")}. {l.label}
                </NavLink>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
